/**
 * Onboarding API Schemas
 *
 * Zod schemas for SaaS onboarding flow.
 * SINGLE SOURCE OF TRUTH for onboarding API contract.
 */

import { z } from 'zod/v4';

// ============================================================================
// Locale
//
// Supported UI/content locales — mirrors apps/ui-worker i18n `supportedLanguages`.
// SSOT for the demo-data language enum (reused by demo-data.ts).
// ============================================================================

export const supportedLocaleSchema = z.enum(['en', 'ru', 'uk', 'es']);

// ============================================================================
// Role (new in preview-first onboarding — collected on Quick Context step)
// ============================================================================

export const onboardingRoleSchema = z.enum([
  'owner',
  'sales_manager',
  'agent',
  'hr',
  'other',
]);

// ============================================================================
// Team Size
// ============================================================================

export const teamSizeSchema = z.enum(['1-10', '11-50', '51+']);

// ============================================================================
// Industry
//
// First block = verticals shown in the new wizard.
// Second block = legacy values kept for backwards-compatibility with orgs
// onboarded before the preview-first redesign; not shown in the new picker.
// ============================================================================

export const industrySchema = z.enum([
  // Current verticals
  'saas',
  'real_estate',
  'call_center',
  'ecommerce',
  'financial_services',
  'healthcare',
  'other',
  // Legacy (still accepted from existing org.settings.industry values)
  'sales',
  'support',
  'e-commerce',
  'finance',
  'education',
]);

// ============================================================================
// SaaS Onboarding Request
//
// Preview-first flow collects organizationName + role/teamSize/industry.
// Both name fields stay optional at the contract level (backend uses
// `My Workspace` / `Default` when absent): the wizard sends organizationName
// but never projectName, and owner preview mode submits nothing at all.
// otherIndustry carries the free-text value when the user picks "Other".
// ============================================================================

export const saasOnboardingRequestSchema = z.object({
  acceptedTerms: z.boolean().refine((val) => val === true, {
    message: 'Terms of Service must be accepted',
  }),
  acceptedPrivacy: z.boolean().refine((val) => val === true, {
    message: 'Privacy Policy must be accepted',
  }),
  // Opt-in to the marketing series. NOT `.literal(true)` like the two above:
  // those are a precondition of using the product, this is a free choice.
  //
  // The default is what makes it safe — a client that omits the field (an older
  // bundle, a replayed request) means "no consent", never "yes". Enrollment into
  // SendPulse is gated on this being explicitly true.
  acceptedMarketing: z.boolean().default(false),
  role: onboardingRoleSchema,
  teamSize: teamSizeSchema,
  industry: industrySchema,
  otherIndustry: z.string().trim().min(1).max(100).optional(),
  // `.trim()` ПЕРЕД границами, а не после: при обратном порядке `"  A  "`
  // проходит проверку длины как пятисимвольная строка и сохраняется как
  // односимвольное имя. Тот же порядок в `demoDataOnboardRequestSchema` —
  // обе схемы кормит один и тот же шаг опроса (`QuickContextStep`), и
  // расходиться в том, что считается допустимым именем, им нельзя.
  organizationName: z.string().trim().min(2).max(100).optional(),
  projectName: z.string().min(2).max(100).trim().optional(),
  // UI locale (i18n.language) — drives the language of auto-seeded demo data so
  // the sample dashboard matches the user's chosen language. `.catch` keeps
  // older clients that omit the field (or send an unexpected value) on 'en'.
  locale: supportedLocaleSchema.catch('en'),
  // Partner referral code captured from `?ref=` on the landing URL. Free-form
  // (no managed partners list yet); constrained to a safe charset so junk never
  // lands in storage. Persisted on the user (metadata.referralCode) and org
  // (settings_json.referredByCode) by the onboarding endpoint.
  referralCode: z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9_-]{1,64}$/)
    .optional(),
});

// ============================================================================
// SaaS Onboarding Response
// ============================================================================

export const saasOnboardingResponseSchema = z.object({
  orgId: z.string(),
  projectId: z.string(),
});

// ============================================================================
// Type Exports
// ============================================================================

export type OnboardingRole = z.infer<typeof onboardingRoleSchema>;
export type TeamSize = z.infer<typeof teamSizeSchema>;
export type Industry = z.infer<typeof industrySchema>;
export type SaaSOnboardingRequest = z.infer<typeof saasOnboardingRequestSchema>;
export type SaaSOnboardingResponse = z.infer<typeof saasOnboardingResponseSchema>;
